import React from 'react';
import { Loader } from 'freemium-ui';
import { SurveyQuestionType, SurveyType } from 'freemium-survey-components';
import { isNil } from 'lodash';
import { useTranslation } from 'react-i18next';
import Consent from 'routes/screens/response/screens/view/components/Consent';
import DateTime from 'routes/screens/response/screens/view/components/datetime';
import { FormFieldResponse } from 'routes/screens/response/screens/view/components/formField';
import { MatrixQuestion } from 'routes/screens/response/screens/view/components/matrix';
import RangeQuestion from 'routes/screens/response/screens/view/components/range';
import SliderQuestion from 'routes/screens/response/screens/view/components/slider';
import {
  StyledFileQn,
  StyledRankOrderItem,
} from 'routes/screens/response/screens/view/style';
import Icon from 'shared/components/Icon';
import { AccountInfoType, SurveyBlockWithQuestionType } from 'shared/types';
import { useAccessControl } from 'utils/access-control';
import request from 'utils/request';

type ResponseAnswerProps = {
  question?: SurveyQuestionType;
  block: SurveyBlockWithQuestionType | undefined;
  isFetchedTranslation?: boolean;
  surveyInfo: SurveyType;
  answer: any;
  accountInfo: AccountInfoType | undefined;
  responsesToShow: any;
  resolvedQuestionAnswer: any;
  responseId?: string;
};

export const ResponseAnswer = (props: ResponseAnswerProps) => {
  const {
    question,
    block,
    isFetchedTranslation,
    surveyInfo,
    answer,
    accountInfo,
    responsesToShow,
    resolvedQuestionAnswer,
    responseId,
  } = props;
  const { access } = useAccessControl();
  const [isDownloadingFile, setisDownloadingFile] =
    React.useState<boolean>(false);
  const { t } = useTranslation();
  const brandColor = accountInfo?.account_meta?.brand_color;

  const respondedQuestionAnswer = responsesToShow.length
    ? responsesToShow.find(
        (answerData: any) => answerData.name === question?.name,
      )
    : null;

  const questionAnswer = respondedQuestionAnswer
    ? respondedQuestionAnswer.answer
    : answer;

  const downloadFile = () => {
    if (
      questionAnswer &&
      access.includes('response:write') &&
      !isDownloadingFile
    ) {
      setisDownloadingFile(true);
      request(
        `/surveys/${surveyInfo.id}/questions/${question?.id}/responses/${responseId}/download-link?fileName=${questionAnswer}`,
      )
        .then((url) => fetch(url))
        .then((response) => response.blob())
        .then((blob) => {
          const link = document.createElement('a');
          const url = URL.createObjectURL(blob);
          link.href = url;
          link.download = questionAnswer;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          setisDownloadingFile(false);
        });
    }
  };

  const answerRenderer = () => {
    switch (question?.type_info?.question_type) {
      case 'BOOLEAN':
      case 'RANGE':
        return (
          <>
            {block?.deleted_on && isNil(questionAnswer) ? (
              ''
            ) : ((!isNil(questionAnswer) && String(questionAnswer) !== '-') ||
                String(questionAnswer) === '0') &&
              question?.id ? (
              <RangeQuestion
                isFetchedTranslation={isFetchedTranslation}
                question={question}
                range={question?.type_info?.scale_properties?.range || []}
                brandColor={brandColor || '#E50914'}
                value={
                  typeof questionAnswer === 'boolean'
                    ? questionAnswer
                      ? '1'
                      : '0'
                    : String(questionAnswer)
                }
                ui_theme={surveyInfo?.ui_theme || {}}
                transVariable={
                  respondedQuestionAnswer?.labels ||
                  question?.type_info?.scale_properties?.labels?.values
                }
              />
            ) : (
              '-'
            )}
          </>
        );
      case 'CONSENT':
        return (
          <>
            <Consent
              surveyInfo={surveyInfo}
              brandColor={brandColor || '#E50914'}
              question={question}
              labels={respondedQuestionAnswer?.labels}
              resolvedQuestionAnswer={String(resolvedQuestionAnswer)}
            />
          </>
        );
      case 'DATE_TIME':
        return (
          <>
            {questionAnswer ? (
              <DateTime answer={questionAnswer} question={question} />
            ) : (
              '-'
            )}
          </>
        );
      case 'MATRIX_RATING_SCALE':
        return (
          <>
            {(respondedQuestionAnswer?.answer || []).length > 0 ? (
              <MatrixQuestion
                respondedQuestionAnswer={respondedQuestionAnswer}
                question={question}
                brandColor={brandColor || '#E50914'}
              />
            ) : (
              '-'
            )}
          </>
        );
      case 'SLIDER':
        return (
          <>
            {!isNil(questionAnswer) ? (
              <SliderQuestion
                question={question}
                answer={resolvedQuestionAnswer}
                brandColor={brandColor || '#E50914'}
                textColor={
                  surveyInfo?.ui_theme?.primary_button_text_color || '#fff'
                }
              />
            ) : (
              '-'
            )}
          </>
        );
      case 'FORM_FIELD':
        return (
          <>
            {questionAnswer ? (
              <FormFieldResponse
                questionAnswer={questionAnswer}
                question={block?.question}
                respondedQuestionAnswerLabels={respondedQuestionAnswer?.labels}
              />
            ) : (
              '-'
            )}
          </>
        );
      case 'CONTACT_FORM':
        return (
          <>
            {questionAnswer ? (
              <FormFieldResponse
                questionAnswer={questionAnswer}
                question={block?.question}
              />
            ) : (
              '-'
            )}
          </>
        );
      case 'RANK_ORDER':
        return (
          <>
            {questionAnswer
              ? questionAnswer?.map?.((c, i) => (
                  <StyledRankOrderItem
                    key={c.id}
                    brandColor={brandColor || '#E50914'}
                    textColor={
                      surveyInfo?.ui_theme?.primary_button_text_color || '#fff'
                    }
                  >
                    <div className="rank-content-container">
                      <div className="item-rank">{i + 1}</div>
                      <div className="item-content">{c}</div>
                    </div>
                  </StyledRankOrderItem>
                ))
              : '-'}
          </>
        );
      case 'FILE':
        return (
          <StyledFileQn isDownloading={isDownloadingFile}>
            {questionAnswer ? (
              <p className="description file-qn">
                <a
                  download={
                    access.includes('response:write') ? questionAnswer : false
                  }
                  onClick={downloadFile}
                >
                  <Icon
                    glyph={'attachment'}
                    fill="none"
                    width={14}
                    viewBox="0 0 20 20"
                    height={20}
                  />
                  {questionAnswer || `${t('no_file_name_found')}`}
                  {isDownloadingFile && <Loader size={'small'} />}
                </a>
              </p>
            ) : (
              '-'
            )}
          </StyledFileQn>
        );
      default:
        return <p className="description">{resolvedQuestionAnswer}</p>;
    }
  };
  return <div>{answerRenderer()}</div>;
};
